/* eslint-disable eqeqeq */
/**
 * Phase 6e2b-1 — drawOnCanvas / drawDrainPanOnCanvas payload + gate logic
 * (accessory_view.js:2032-2116, parity 334). Pure functions: the chassis
 * keeps the JSON snapshot + isAlredyRendered flag and emits
 * `draw:accessory:on:canvas` / `remove_accessory_from_canvas`.
 *
 * The canvasData field set, the remove rules and the frozen positional arg
 * order are copied VERBATIM — this snapshot diff is the only thing standing
 * between form renders and canvas redraw storms.
 */

export interface AccessoryCanvasBuild {
  canvasData: Record<string, unknown>;
  drawLabel: string;
  viewport: Record<string, unknown> | null;
  intWall: Record<string, unknown> | null;
  lightParams: Record<string, unknown> | null;
}

const safeParse = (raw: unknown): Record<string, unknown> | null => {
  if (!raw || typeof raw !== 'string') return null;
  try {
    return JSON.parse(raw) as Record<string, unknown>;
  } catch {
    return null;
  }
};

/** accessory_view.js:2032-2063 — the canvasData snapshot object. */
export function buildAccessoryCanvasData(args: {
  model: Record<string, unknown>;
  position: Record<string, unknown>;
  type: string;
  cmsAccessory: Record<string, unknown> | undefined;
  /** §49.6:2059 — plane>6 int-wall lookup (damper/louver dataGetter, 6e2b-2). */
  internalWalls?: Array<Record<string, unknown>>;
}): AccessoryCanvasBuild {
  const { model, position, type, cmsAccessory, internalWalls } = args;
  const damperParams = safeParse(model.damper_params);
  const viewportParams = safeParse(model.viewport_params);
  const cmsViewportModels =
    ((cmsAccessory?.viewport as Record<string, unknown> | undefined)?.model as
      | Array<Record<string, unknown>>
      | undefined) ?? [];
  const planeId = Number(position.mounting_plane_id) || null;
  const canvasData: Record<string, unknown> = {
    // dev-sync (accessory_view.js:2043): section_id joined the snapshot so a
    // section MOVE (the VFD selector's Section select) re-fires the draw; the
    // emit's section arg already resolves model.section_id first
    // (useAccessoryForm — the dev drawSection equivalent).
    section_id: model.section_id,
    tag: model.tag,
    mounting_plane: position.mounting_plane_id,
    mounting_type: position.mounting_type,
    x: position.x,
    y: position.y,
    distance_to_downstream_wall: model.distance_to_downstream_wall,
    distance_to_roof: model.distance_to_roof,
    distance_to_right_wall: model.distance_to_right_wall,
    height: model.height || model.overall_depth || model.height_calc,
    height_2: model.height_2,
    width: model.width || model.overall_width || model.width_calc,
    depth: model.depth ? model.depth : null,
    // COMBO LOUVER — A2.1 (accessory_view.js:2058-2064, verbatim, same slot):
    // "the enclosure box + face louvers are driven by these fields, so include
    // them in the change-detection snapshot or the canvas won't redraw when the
    // user resizes the enclosure or toggles faces." A1.6 landed the enclosure
    // PATCH (LouverForm's ComboEnclosureRow) but with these keys absent the
    // snapshot was identical after an edit, so the draw gate never fired.
    // NB `combo_faces` is stringified because the raw value is the serializer's
    // ARRAY and this snapshot is compared via JSON.stringify — the canvas gets
    // the array off `model.toJSON()`, never this copy (inventory DEAD-3).
    enclosure_width: model.enclosure_width,
    enclosure_height: model.enclosure_height,
    enclosure_depth: model.enclosure_depth,
    combo_faces: model.combo_faces ? JSON.stringify(model.combo_faces) : null,
    // ⚠ DEDRIFT (deliberate): legacy's snapshot does NOT carry `is_combo`
    // (:2044-2080 has no such key). Added because the combo geometry branch
    // keys off it and a snapshot that tracks the draw's inputs is the safer
    // shape. Provably INERT for the diff: `is_combo` never transitions in a
    // session (no control converts a louver to/from combo — inventory DEAD-1),
    // so the key is a constant in both the old and the new JSON and every
    // `json != prevJson` outcome is unchanged.
    is_combo: model.is_combo,
    angle_1: model.angle_1 ? model.angle_1 : null,
    angle_2: model.angle_2 ? model.angle_2 : null,
    angle_3: model.angle_3 ? model.angle_3 : null,
    angle_4: model.angle_4 ? model.angle_4 : null,
    style_id: model.style_id ? model.style_id : null,
    casing_mount: model.casing_mount_id ? model.casing_mount_id : null,
    blade_direction: model.blade_direction_id ? model.blade_direction_id : null,
    blade_orientation_id: damperParams?.blade_orientation_id,
    orientation_id: model.orientation_id ? model.orientation_id : null,
    viewport_params:
      model.viewport_params &&
      cmsViewportModels.find((el) => el.model == viewportParams?.model)
        ? viewportParams?.model
        : null,
    int_wall:
      planeId != null && planeId > 6
        ? internalWalls?.find((el) => el.id === planeId)
        : null,
    base_height: model.base_height,
    base_type: model.base_type,
    casing_insulation_thick: model.casing_insulation_thick,
    // dev-sync F Stage 2 (accessory_view.js:2078): air_direction_id joined the
    // change-detection snapshot so the canvas redraws when Air Direction
    // changes. CURRENTLY UNUSED by React's canvas — it becomes load-bearing
    // only when the combo-louver 3-D face code lands (canvas.view3d.js:3269-3286
    // reads accessory.air_direction_id). Ported now for data-flow parity.
    air_direction_id: model.air_direction_id,
  };
  const lightParams = safeParse(model.light_params);
  const isLinearLight = type === 'light' && lightParams?.style_id == 2;
  return {
    canvasData,
    drawLabel: `${type}${isLinearLight ? '_LINEAR' : ''}`,
    viewport: canvasData.viewport_params ? safeParse(model.viewport_params) : null,
    intWall: (canvasData.int_wall as Record<string, unknown> | null | undefined) ?? null,
    lightParams,
  };
}

export interface DrawGateResult {
  draw: boolean;
  remove: boolean;
  resetRendered: boolean;
}

/** accessory_view.js:2064-2088 — REMOVE paths + DRAW gate, verbatim. */
export function accessoryDrawGate(
  type: string,
  built: AccessoryCanvasBuild,
  json: string,
  prevJson: string | null,
  isAlreadyRendered: boolean,
  modelForChecks?: Record<string, unknown>
): DrawGateResult {
  const cd = built.canvasData;
  const model = modelForChecks ?? {};
  const prev = prevJson ? (JSON.parse(prevJson) as Record<string, unknown>) : null;
  const label = String((model.label as string) || '');

  // checkForLouverAndDamper (:2065): plane 9 OR blade invalid for new plane.
  const bladeDirections = model.blade_directions as
    | Record<string, Array<Record<string, unknown>>>
    | undefined;
  const checkForLouverAndDamper =
    cd.mounting_plane == 9 ||
    !!(
      prev?.blade_direction &&
      cd.mounting_plane &&
      !bladeDirections?.[String(cd.mounting_plane)]?.find((el) => el.id == cd.blade_direction) &&
      (label.startsWith('Damper') || label.startsWith('Louver'))
    );
  // checkForLinerLight (:2066): linear light orientation invalid for plane.
  const orientations = model.orientations as
    | Record<string, Array<Record<string, unknown>>>
    | undefined;
  const checkForLinearLight =
    type === 'light' &&
    built.lightParams?.style_id == 2 &&
    !!prev?.orientation_id &&
    !!cd.mounting_plane &&
    !orientations?.[String(cd.mounting_plane)]?.find((el) => el.id == cd.orientation_id);
  // checkForHoodStyle4NoHeight2 (:2067).
  const checkForHoodStyle4 = type === 'hood' && cd.style_id == 4 && cd.height_2 == null;
  // int_wall vanished (:2068 first clause).
  const intWallVanished = cd.int_wall === undefined && !!prev?.int_wall;
  // has-vfd-fix — Annexair fans ship with the VFD built into the motor
  // housing: a VFD auto-created from that fan's has_vfd checkbox is real for
  // BOM/pricing (model.skip_3d_model comes from AHUAccessoryVFD.skip_3d_model)
  // but must never be drawn as a separate box, matching the FreeCAD
  // generator's ahu.xml template.
  const skipAnnexairFanVfd = type === 'vfd' && !!model.skip_3d_model;
  // has-vfd-fix — fan VFD accessories are entirely driven by the fan's
  // has_vfd checkbox (model.check_vfd comes from AHUAccessoryVFD.check_vfd,
  // false whenever the owning fan's fan_params.has_vfd is false/absent, or
  // the VFD isn't linked via fan_motor at all -- e.g. legacy rows only
  // carrying the separate `fan` FK, which can't be caught by master_vfd_id
  // alone since it stays set on those). Mirrors the check_vfd gate the
  // FreeCAD generator's ahu.xml template already applies. Without this, a box
  // drawn while has_vfd was checked never gets removed again once unchecked.
  const hideUncheckedVfd = type === 'vfd' && !model.check_vfd;

  if (
    intWallVanished ||
    checkForLouverAndDamper ||
    checkForLinearLight ||
    checkForHoodStyle4 ||
    skipAnnexairFanVfd ||
    hideUncheckedVfd
  ) {
    return { draw: false, remove: true, resetRendered: true };
  }

  const bladeError =
    label.startsWith('Damper') || label.startsWith('Louver') ? !cd.blade_direction : false;
  const orientationError =
    type === 'light' && built.lightParams?.style_id == 2 ? !cd.orientation_id : false;
  const resetRendered = isAlreadyRendered && (!cd.height || !cd.width);

  let draw: boolean;
  if (label.startsWith('Hood')) {
    draw =
      json != prevJson &&
      !!cd.mounting_plane &&
      !!cd.height &&
      !!cd.width &&
      !!(cd.angle_1 || cd.angle_2 || cd.angle_3 || cd.angle_4);
  } else {
    draw =
      !orientationError &&
      !bladeError &&
      json != prevJson &&
      !!cd.mounting_plane &&
      !!cd.height &&
      !!cd.width &&
      ((type === 'vfd' && !!model.master_vfd_id && !!model.check_vfd) || type !== 'vfd');
  }
  return { draw, remove: false, resetRendered };
}

/** drawDrainPanOnCanvas (:2090-2116) — dim-based snapshot + gate. */
export function buildDrainPanCanvasData(model: Record<string, unknown>): {
  canvasData: Record<string, unknown>;
  gatePassed: boolean;
} {
  const canvasData = {
    distance_from_section_ups: model.distance_from_section_ups,
    dim10_upper_pan_side_wall_height: model.dim10_upper_pan_side_wall_height,
    dim1_length: model.dim1_length,
    dim11_upper_pan_ups_wall_height: model.dim11_upper_pan_ups_wall_height,
    dim7_upper_pan_length: model.dim7_upper_pan_length,
    width: model.width,
    // dev-sync 037dba7a32 (accessory_view.js:2097-2115): the slope-direction
    // keys gave way to the profile dims the redrawn pan is built from — the
    // connection distance included, so editing Connection Position /
    // Connection Distance from Ups now moves the snapshot and fires a redraw.
    // (The legacy list also carries `vert_pos_id` and
    // `connection_height_from_floor`; neither is on the drain-pan serializer,
    // so both are always undefined and JSON-dropped — omitted here. Their
    // live counterpart `vert_pos` only moves with the Type, which already
    // moves `depth` and the flange dims above.)
    depth: model.depth,
    dp_conn_distance_from_ups: model.dp_conn_distance_from_ups,
    ups_vertical_flange: model.ups_vertical_flange,
    ups_horizontal_flange: model.ups_horizontal_flange,
    ups_down_wall: model.ups_down_wall,
    down_vertical_flange: model.down_vertical_flange,
    down_horizontal_lip: model.down_horizontal_lip,
    down_horizontal_flange: model.down_horizontal_flange,
    conn_hand: model.conn_hand,
    pipe_nipple_od_dia: model.pipe_nipple_od_dia,
    conn_protrusion_from_base: model.conn_protrusion_from_base,
    conn_hand_id: model.conn_hand_id,
    tag: model.tag,
    // dev-sync (accessory_view.js:2125): dead key on drain pans (always
    // undefined → JSON-dropped) — kept verbatim for snapshot parity.
    housed_in_enclosure: model.housed_in_enclosure,
  };
  return { canvasData, gatePassed: !!canvasData.dim1_length };
}
